import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { MapPin, TrendingUp, TrendingDown, Crown, AlertTriangle } from 'lucide-react';
import { useRest, RCard, RHead, RestLoading, RestReconnect, money } from './restui';

function Spark({ daily, color = 'var(--rest-accent)' }) {
  if (!daily || daily.length < 2) return null;
  const vals = daily.map((d) => d.total);
  const max = Math.max(...vals) || 1;
  const min = Math.min(...vals);
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * 100},${28 - ((v - min) / (max - min || 1)) * 24}`).join(' ');
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-8">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* SVG coverage map — pins projected from lat/lon, sized by sales share. */
function LocMap({ locs, isEs, focus, setFocus }) {
  const pts = locs.filter((l) => l.lat != null && l.lon != null);
  if (pts.length < 2) return null;
  const lats = pts.map((l) => l.lat); const lons = pts.map((l) => l.lon);
  const mnLa = Math.min(...lats); const mxLa = Math.max(...lats);
  const mnLo = Math.min(...lons); const mxLo = Math.max(...lons);
  const W = 680; const Hh = 260; const pad = 52;
  const px = (lon) => pad + (mxLo === mnLo ? 0.5 : (lon - mnLo) / (mxLo - mnLo)) * (W - 2 * pad);
  const py = (lat) => pad - 10 + (mxLa === mnLa ? 0.5 : (mxLa - lat) / (mxLa - mnLa)) * (Hh - 2 * pad);
  const maxShare = Math.max(...pts.map((l) => l.share_pct)) || 1;
  return (
    <RCard className="p-5 mb-5" testid="rest-locations-map">
      <p className="text-[12px] font-bold text-gray-700">{isEs ? 'Mapa de cobertura · NY & CT' : 'Coverage map · NY & CT'}</p>
      <p className="mt-0.5 text-[11px] text-gray-400 mb-2">
        {isEs ? 'El tamaño del pin refleja la cuota de ventas del periodo. Toca un pin para ver el detalle de ese local.' : 'Pin size reflects the period sales share. Tap a pin to see that location\u2019s detail.'}
      </p>
      <svg viewBox={`0 0 ${W} ${Hh}`} className="w-full h-auto select-none">
        <rect x="0" y="0" width={W} height={Hh} rx="14" fill="#F7FAF8" />
        {[1, 2, 3].map((i) => <line key={`h${i}`} x1="0" y1={(Hh / 4) * i} x2={W} y2={(Hh / 4) * i} stroke="#E8EEEA" strokeWidth="1" />)}
        {[1, 2, 3, 4, 5].map((i) => <line key={`v${i}`} x1={(W / 6) * i} y1="0" x2={(W / 6) * i} y2={Hh} stroke="#E8EEEA" strokeWidth="1" />)}
        <text x={W - 14} y={Hh - 10} textAnchor="end" fontSize="9.5" fill="#B8C4BD">{isEs ? 'Posición aproximada por coordenadas' : 'Approximate position by coordinates'}</text>
        {pts.map((l) => {
          const x = px(l.lon); const y = py(l.lat);
          const rr = 7 + 9 * (l.share_pct / maxShare);
          const active = focus === l.name;
          return (
            <g key={l.name} onClick={() => setFocus(focus === l.name ? 'all' : l.name)} style={{ cursor: 'pointer' }}
              data-testid={`rest-locmap-pin-${l.name.toLowerCase().replace(/ /g, '-')}`}>
              <circle cx={x} cy={y} r={rr} fill="var(--rest-accent)" opacity={active ? 0.95 : 0.55} stroke="#fff" strokeWidth="2" />
              <circle cx={x} cy={y} r="2.5" fill="#fff" />
              <text x={x} y={y - rr - 5} textAnchor="middle" fontSize="11" fontWeight="700" fill={active ? 'var(--rest-accent)' : '#374151'}>{l.name}</text>
              <text x={x + rr + 5} y={y + 3.5} textAnchor="start" fontSize="10" fontWeight="600" fill="#6B7280">{`${l.share_pct}%`}</text>
            </g>
          );
        })}
      </svg>
    </RCard>
  );
}

/* Head-to-head comparison — pick any two locations. */
function CompareCard({ locs, isEs }) {
  const [a, setA] = useState(locs[0]?.name || '');
  const [b, setB] = useState(locs[1]?.name || '');
  const A = locs.find((l) => l.name === a);
  const B = locs.find((l) => l.name === b);
  if (!A || !B) return null;
  const gap = B.revenue ? Math.round(1000 * (A.revenue - B.revenue) / B.revenue) / 10 : null;
  const rows = [
    [isEs ? 'Ventas del periodo' : 'Period revenue', money(A.revenue), money(B.revenue), A.revenue - B.revenue],
    [isEs ? 'Pedidos' : 'Orders', A.orders.toLocaleString(), B.orders.toLocaleString(), A.orders - B.orders],
    [isEs ? 'Ticket medio' : 'Avg ticket', money(A.avg_ticket), money(B.avg_ticket), A.avg_ticket - B.avg_ticket],
    [isEs ? 'Cuota del grupo' : 'Group share', `${A.share_pct}%`, `${B.share_pct}%`, A.share_pct - B.share_pct],
    [isEs ? 'vs periodo anterior' : 'vs previous period',
      A.delta_pct == null ? '—' : `${A.delta_pct >= 0 ? '+' : ''}${A.delta_pct}%`,
      B.delta_pct == null ? '—' : `${B.delta_pct >= 0 ? '+' : ''}${B.delta_pct}%`,
      (A.delta_pct || 0) - (B.delta_pct || 0)],
    [isEs ? 'Plato top' : 'Top dish', A.top_item || '—', B.top_item || '—', 0],
  ];
  const selCls = 'rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-[12.5px] font-semibold text-gray-700';
  return (
    <RCard className="p-5 mb-5" testid="rest-locations-compare">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <p className="text-[12px] font-bold text-gray-700 mr-1">{isEs ? 'Comparador: dos locales cara a cara' : 'Head-to-head: two locations'}</p>
        <select value={a} onChange={(e) => setA(e.target.value)} data-testid="rest-locations-compare-a" className={selCls}>
          {locs.map((l) => <option key={l.name} value={l.name}>{l.name}</option>)}
        </select>
        <span className="text-[11px] font-black text-gray-400">VS</span>
        <select value={b} onChange={(e) => setB(e.target.value)} data-testid="rest-locations-compare-b" className={selCls}>
          {locs.map((l) => <option key={l.name} value={l.name}>{l.name}</option>)}
        </select>
      </div>
      {a === b ? (
        <p className="text-[12.5px] text-gray-400">{isEs ? 'Elige dos locales distintos para compararlos.' : 'Pick two different locations to compare.'}</p>
      ) : (
        <>
          {gap !== null && (
            <p className="mb-3 text-[12.5px] text-gray-600" data-testid="rest-locations-compare-verdict">
              {gap >= 0
                ? (isEs ? <><b className="text-gray-900">{A.name}</b> vende un <b className="text-[var(--rest-accent)]">{gap}%</b> más que <b>{B.name}</b> en este periodo.</>
                  : <><b className="text-gray-900">{A.name}</b> sells <b className="text-[var(--rest-accent)]">{gap}%</b> more than <b>{B.name}</b> this period.</>)
                : (isEs ? <><b className="text-gray-900">{B.name}</b> vende un <b className="text-[var(--rest-accent)]">{Math.abs(gap)}%</b> más que <b>{A.name}</b> en este periodo.</>
                  : <><b className="text-gray-900">{B.name}</b> sells <b className="text-[var(--rest-accent)]">{Math.abs(gap)}%</b> more than <b>{A.name}</b> this period.</>)}
            </p>
          )}
          <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1.2fr_1fr_1fr] gap-x-4">
            <span />
            <span className="text-[11.5px] font-bold text-gray-800 text-right pb-1.5">{A.name}</span>
            <span className="text-[11.5px] font-bold text-gray-800 text-right pb-1.5">{B.name}</span>
            {rows.map(([label, va, vb, diff], i) => (
              <React.Fragment key={label}>
                <span className={`text-[12px] text-gray-500 py-1.5 ${i ? 'border-t border-gray-100' : ''}`}>{label}</span>
                <span className={`text-[12.5px] tabular-nums text-right py-1.5 ${i ? 'border-t border-gray-100' : ''} ${diff > 0 ? 'font-black text-[var(--rest-accent)]' : 'font-semibold text-gray-700'}`}>{va}</span>
                <span className={`text-[12.5px] tabular-nums text-right py-1.5 ${i ? 'border-t border-gray-100' : ''} ${diff < 0 ? 'font-black text-[var(--rest-accent)]' : 'font-semibold text-gray-700'}`}>{vb}</span>
              </React.Fragment>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-4">
            <div><p className="text-[10.5px] text-gray-400 mb-0.5">{A.name}</p><Spark daily={A.daily} /></div>
            <div><p className="text-[10.5px] text-gray-400 mb-0.5">{B.name}</p><Spark daily={B.daily} color="#9CA3AF" /></div>
          </div>
          <p className="mt-2 text-[10.5px] text-gray-400">{isEs ? 'Comparativa del periodo seleccionado arriba. Datos estimados del dataset demo.' : 'Comparison for the period selected above. Estimated demo dataset.'}</p>
        </>
      )}
    </RCard>
  );
}

export default function RestLocations() {
  const { isEs } = useOutletContext();
  const [days, setDays] = useState(30);
  const [focus, setFocus] = useState('all');
  const { data: d, err, loading } = useRest(`/locations?days=${days}`);
  if (loading && !d) return <RestLoading />;
  if (err) return <RestReconnect />;
  if (!d.has_locations) {
    return (
      <div data-testid="rest-locations">
        <RHead title={isEs ? 'Locales' : 'Locations'} />
        <RCard className="p-10 text-center"><p className="text-gray-500 text-[14px]">{isEs ? 'Este restaurante tiene un solo local o los datos no incluyen desglose por local.' : 'This restaurant has a single location or the data has no per-location detail.'}</p></RCard>
      </div>
    );
  }
  const best = d.locations[0];
  const sel = focus !== 'all' ? d.locations.find((l) => l.name === focus) : null;
  const worst = [...d.locations].filter((l) => l.delta_pct !== null).sort((a, b) => a.delta_pct - b.delta_pct)[0];
  const climber = [...d.locations].filter((l) => l.delta_pct !== null).sort((a, b) => b.delta_pct - a.delta_pct)[0];
  return (
    <div data-testid="rest-locations">
      <RHead title={isEs ? 'Locales' : 'Locations'}
        sub={isEs ? `Comparativa de ventas · ${d.from} → ${d.to}` : `Sales comparison · ${d.from} → ${d.to}`}
        right={
          <div className="flex items-center gap-2">
            <select value={focus} onChange={(e) => setFocus(e.target.value)} data-testid="rest-locations-focus"
              className="rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-[12.5px] text-gray-600">
              <option value="all">{isEs ? 'Todos los locales' : 'All locations'}</option>
              {d.locations.map((l) => <option key={l.name} value={l.name}>{l.name}</option>)}
            </select>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="rest-locations-range"
              className="rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-[12.5px] text-gray-600">
              <option value={7}>{isEs ? '7 días' : '7 days'}</option>
              <option value={30}>{isEs ? '30 días' : '30 days'}</option>
              <option value={90}>{isEs ? '90 días' : '90 days'}</option>
              <option value={365}>{isEs ? '12 meses' : '12 months'}</option>
            </select>
          </div>
        } />

      <RCard className="p-5 mb-5" data-testid="rest-locations-summary">
        <p className="text-[12px] text-gray-500">
          {isEs
            ? <>Ventas totales de los {d.locations.length} locales: <b className="text-gray-900">{money(d.total)}</b>. El local líder es <b className="text-gray-900">{best.name}</b> con el {best.share_pct}% del total.</>
            : <>Total sales across {d.locations.length} locations: <b className="text-gray-900">{money(d.total)}</b>. The leading location is <b className="text-gray-900">{best.name}</b> with {best.share_pct}% of the total.</>}
        </p>
      </RCard>

      <LocMap locs={d.locations} isEs={isEs} focus={focus} setFocus={setFocus} />

      {worst && worst.delta_pct < -2 && (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center gap-2" data-testid="rest-locations-alert">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
          <p className="text-[12.5px] text-amber-800">
            {isEs
              ? <><b>{worst.name}</b> vende <b>{worst.delta_pct}%</b> menos que el periodo anterior. Rachel sugiere revisar horas pico, reseñas y delivery de ese local.</>
              : <><b>{worst.name}</b> is selling <b>{worst.delta_pct}%</b> less than the previous period. Rachel suggests reviewing peak hours, reviews and delivery for that location.</>}
          </p>
          {climber && climber.delta_pct > 0 && (
            <p className="text-[11.5px] text-emerald-700 ml-auto">
              ▲ {isEs ? 'El que más crece' : 'Fastest climber'}: <b>{climber.name}</b> +{climber.delta_pct}%
            </p>
          )}
        </div>
      )}

      {sel && (
        <RCard className="p-6 mb-5 border-[var(--rest-accent)]/40" data-testid="rest-location-focus">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold text-[var(--rest-accent)] flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {sel.name} · {isEs ? 'detalle' : 'detail'}</p>
              <p className="mt-1 text-[26px] font-bold text-gray-900 tracking-tight">{money(sel.revenue)}</p>
              <p className="text-[12px] text-gray-500">{sel.address}</p>
            </div>
            <div className="grid grid-cols-3 gap-5 text-[12px]">
              <div><p className="text-gray-400">{isEs ? 'Pedidos' : 'Orders'}</p><p className="font-bold text-gray-900 text-[15px]">{sel.orders.toLocaleString()}</p></div>
              <div><p className="text-gray-400">{isEs ? 'Ticket medio' : 'Avg ticket'}</p><p className="font-bold text-gray-900 text-[15px]">{money(sel.avg_ticket)}</p></div>
              <div><p className="text-gray-400">{isEs ? 'Cuota del grupo' : 'Group share'}</p><p className="font-bold text-gray-900 text-[15px]">{sel.share_pct}%</p></div>
            </div>
          </div>
          <div className="mt-4 h-20"><Spark daily={sel.daily} /></div>
          <p className="mt-2 text-[12px] text-gray-500">
            ⭐ {isEs ? 'Más vendido' : 'Top seller'}: <b className="text-gray-800">{sel.top_item}</b>
            {sel.delta_pct !== null && <> · {sel.delta_pct >= 0 ? '▲' : '▼'} <b className={sel.delta_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}>{sel.delta_pct >= 0 ? '+' : ''}{sel.delta_pct}%</b> {isEs ? 'vs periodo anterior' : 'vs previous period'}</>}
            {sel.name !== best.name && <> · {isEs ? 'vs líder' : 'vs leader'} ({best.name}): <b className="text-gray-800">{Math.round(100 * sel.revenue / best.revenue)}%</b></>}
          </p>
        </RCard>
      )}

      <RCard className="p-5 mb-5" data-testid="rest-locations-ranking">
        <p className="text-[12px] font-bold text-gray-700 mb-3">{isEs ? 'Ranking: quién vende más y quién menos' : 'Ranking: who sells more, who sells less'}</p>
        <div className="space-y-2.5">
          {d.locations.map((l, i) => (
            <button key={l.name} onClick={() => setFocus(focus === l.name ? 'all' : l.name)} data-testid={`rest-locations-rank-${i + 1}`}
              className="w-full flex items-center gap-3 text-left group">
              <span className="w-5 text-[12px] font-bold text-gray-400">{i + 1}º</span>
              <span className="w-28 sm:w-32 shrink-0 text-[12.5px] font-semibold text-gray-800 truncate group-hover:text-[var(--rest-accent)] transition-colors">{l.name}</span>
              <span className="flex-1 h-4 rounded-full bg-gray-100 overflow-hidden">
                <span className="block h-full rounded-full bg-[var(--rest-accent)] transition-[width] duration-500" style={{ width: `${Math.max(4, 100 * l.revenue / best.revenue)}%`, opacity: 1 - i * 0.15 }} />
              </span>
              <span className="w-24 text-right text-[12px] font-bold tabular-nums text-gray-900">{money(l.revenue)}</span>
              <span className={`w-14 text-right text-[11px] font-bold tabular-nums ${l.delta_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{l.delta_pct >= 0 ? '+' : ''}{l.delta_pct}%</span>
            </button>
          ))}
        </div>
        <p className="mt-2.5 text-[10.5px] text-gray-400">{isEs ? 'Toca un local para ver su detalle. Datos estimados del dataset demo.' : 'Tap a location to see its detail. Estimated demo dataset.'}</p>
      </RCard>

      <CompareCard locs={d.locations} isEs={isEs} />

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {d.locations.map((l, i) => (
          <RCard key={l.name} className="p-5" data-testid={`rest-location-card-${l.name.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[var(--rest-accent)]" />
                <p className="font-bold text-[14.5px] text-gray-900">{l.name}</p>
              </div>
              {i === 0 && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-600 text-[10px] font-bold px-2 py-0.5"><Crown className="w-3 h-3" /> {isEs ? 'Líder' : 'Top'}</span>}
            </div>
            <p className="mt-0.5 text-[11px] text-gray-400 truncate">{l.address}</p>
            <p className="mt-3 text-[22px] font-bold text-gray-900 tracking-tight">{money(l.revenue)}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[11.5px] text-gray-500">{l.share_pct}% {isEs ? 'del total' : 'of total'}</span>
              {l.delta_pct !== null && (
                <span className={`inline-flex items-center gap-0.5 text-[11.5px] font-bold ${l.delta_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {l.delta_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {l.delta_pct >= 0 ? '+' : ''}{l.delta_pct}%
                </span>
              )}
            </div>
            <div className="mt-2"><Spark daily={l.daily} /></div>
            <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-[11.5px]">
              <div><p className="text-gray-400">{isEs ? 'Pedidos' : 'Orders'}</p><p className="font-bold text-gray-800">{l.orders.toLocaleString()}</p></div>
              <div><p className="text-gray-400">{isEs ? 'Ticket medio' : 'Avg ticket'}</p><p className="font-bold text-gray-800">{money(l.avg_ticket)}</p></div>
            </div>
            {l.top_item && (
              <p className="mt-2.5 text-[11.5px] text-gray-500">⭐ {isEs ? 'Más vendido' : 'Top seller'}: <b className="text-gray-800">{l.top_item}</b></p>
            )}
          </RCard>
        ))}
      </div>

      {/* share bar */}
      <RCard className="p-5 mt-5" data-testid="rest-locations-sharebar">
        <p className="text-[12px] font-bold text-gray-700 mb-3">{isEs ? 'Reparto de ventas entre locales' : 'Sales split across locations'}</p>
        <div className="flex h-5 rounded-full overflow-hidden">
          {d.locations.map((l, i) => (
            <div key={l.name} title={`${l.name} · ${l.share_pct}%`}
              style={{ width: `${l.share_pct}%`, opacity: 1 - i * 0.18, background: 'var(--rest-accent)' }} />
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          {d.locations.map((l, i) => (
            <span key={l.name} className="text-[11px] text-gray-500 inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: 'var(--rest-accent)', opacity: 1 - i * 0.18 }} />
              {l.name} · {l.share_pct}%
            </span>
          ))}
        </div>
      </RCard>
    </div>
  );
}
